<template lang="pug">
three(@init-scene="initScene", @tick="tick")
</template>

<script>
import ThreeComponent from "../util/Three";
import * as THREE from "three";
import { GPUComputationRenderer } from "three/examples/jsm/misc/GPUComputationRenderer";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import jacobiShader from "./jacobi_shader.glsl";
import colorShader from "./color_shader.glsl";
import GraphMesh from "../util/GraphMesh";
import CoilBufferGeometry from "./CoilBufferGeometry";
import pizzaGLTF from "../../models/pizza.gltf";

import { RectAreaLightHelper } from "three/examples/jsm/helpers/RectAreaLightHelper";
import { RectAreaLightUniformsLib } from "three/examples/jsm/lights/RectAreaLightUniformsLib";
import { MeshBasicMaterial } from "three";

const n = 64;

const MINI_PIZZA_DISTANCE = 3;
const MINI_PIZZA_SCALE = 0.55;
const MINI_PIZZAS = [0, 1, 2, 3].map((i) => {
  const a = (Math.PI / 2) * (0.5 + i);
  return [Math.cos(a), Math.sin(a)];
});
const PIZZA_RELATIVE_RADIUS = 0.28;
const PIZZA_RELATIVE_DISTANCE = 0.3;

export default {
  props: {
    door: {
      type: Boolean,
      default: false,
    },
    heaters: {
      type: Array,
      default: () => [false, true, true],
      validator(value) {
        return value.length == 3;
      },
    },
    pizza: {
      type: Number,
      default: 4,
      validator: (value) => {
        return [0, 1, 4].indexOf(value) !== -1;
      },
    },
    speed: {
      type: Number,
      default: 1,
    },
  },
  components: {
    three: ThreeComponent,
  },
  computed: {
    sides() {
      return [this.door ? 0 : -1, ...this.heaters.map((v) => (v ? 1 : -1))];
    },
  },
  watch: {
    pizza() {
      this.updatePizza();
    },
  },
  methods: {
    initPizzaCoefficient() {
      const cAir = 1;
      const cPizza = 0.001;
      let rr = PIZZA_RELATIVE_RADIUS * PIZZA_RELATIVE_RADIUS;
      if (this.pizza == 4) rr *= MINI_PIZZA_SCALE * MINI_PIZZA_SCALE;

      const data = this.cTexture.image.data;
      this.cTexture.needsUpdate = true;

      const read = new Float32Array(4 * n * n);
      this.three.renderer.readRenderTargetPixels(
        this.floatTarget,
        0,
        0,
        n,
        n,
        read
      );

      this.tempTexture.needsUpdate = true;
      const temperatureData = this.tempTexture.image.data;

      const inPizza = (x, y) => {
        switch (this.pizza) {
          case 1:
            return x * x + y * y < rr;
          case 4:
            for (const [px, py] of MINI_PIZZAS) {
              px *= PIZZA_RELATIVE_DISTANCE;
              py *= PIZZA_RELATIVE_DISTANCE;
              if ((x - px) * (x - px) + (y - py) * (y - py) < rr) return true;
            }
            return false;
          default:
            return false;
        }
      };

      let k = 0;
      for (let i = 0; i < n; ++i) {
        const x = (i + 0.5) / n - 0.5;
        for (let j = 0; j < n; ++j) {
          const y = (j + 0.5) / n - 0.5;

          const p = inPizza(x, y);
          data[k] = p ? cPizza : cAir;
          temperatureData[k] = p ? 0 : read[k];

          data[k + 1] = 0;
          data[k + 2] = 0;
          data[k + 3] = 1;
          temperatureData[k + 1] = 0;
          temperatureData[k + 2] = 0;
          temperatureData[k + 3] = 1;

          k += 4;
        }
      }
      this.jacobiCompute.renderTexture(this.tempTexture, this.floatTarget);
    },
    jacobi(dt) {
      const uniforms = this.jacobiVar.material.uniforms;

      uniforms.sides.value = this.sides;
      uniforms.fx.value = dt * n * n;
      uniforms.fy.value = dt * n * n;
      uniforms.time.value = (+new Date() / 1000) % 1;
      uniforms.v.value = this.floatTarget.texture;

      let fromMaterial = this.floatTarget.texture;
      for (let i = 0; i < 100; ++i) {
        if (i > 0) {
          fromMaterial = this.jacobiCompute.getCurrentRenderTarget(
            this.jacobiVar
          ).texture;
        }
        uniforms.u0.value = fromMaterial;

        this.jacobiCompute.compute();
      }
      this.jacobiCompute.renderTexture(
        this.jacobiCompute.getCurrentRenderTarget(this.jacobiVar).texture,
        this.floatTarget
      );
    },
    initCompute(three) {
      const jacobiCompute = new GPUComputationRenderer(n, n, three.renderer);
      this.cTexture = jacobiCompute.createTexture();
      this.tempTexture = jacobiCompute.createTexture();
      const jacobiTexture = jacobiCompute.createTexture();
      const floatTarget = jacobiCompute.createRenderTarget(
        undefined,
        undefined,
        undefined,
        undefined,
        THREE.LinearFilter,
        THREE.LinearFilter
      );
      const colorTarget = jacobiCompute.createRenderTarget(
        undefined,
        undefined,
        undefined,
        undefined,
        THREE.LinearFilter,
        THREE.LinearFilter
      );

      const jacobiVar = jacobiCompute.addVariable(
        "jacobi",
        jacobiShader,
        jacobiTexture
      );
      jacobiVar.material.uniforms = {
        sides: { value: [-1, -1, -1, -1] },
        fx: { value: 0 },
        fy: { value: 0 },
        time: { value: 0 },
        v: { value: null },
        u0: { value: null },
        c: { value: this.cTexture },
      };

      this.floatTarget = floatTarget;
      this.jacobiVar = jacobiVar;
      this.jacobiCompute = jacobiCompute;
      this.initPizzaCoefficient();

      const error = jacobiCompute.init();
      if (error !== null) {
        console.error(error);
      }

      this.colorTarget = colorTarget;
      this.colorShader = jacobiCompute.createShaderMaterial(colorShader, {
        floatTexture: { value: floatTarget.texture },
      });
    },
    initGraph(three) {
      const graphMaterial = new THREE.MeshBasicMaterial({
        map: this.colorTarget.texture,
        side: THREE.DoubleSide,
      });
      const graph = new GraphMesh(
        10,
        10,
        2,
        50,
        50,
        graphMaterial,
        this.floatTarget.texture
      );
      graph.rotateX(-Math.PI / 2);
      graph.position.y = 4;
      this.scene.add(graph);
    },
    updatePizza() {
      if (!this.newPizzaObject) {
        if (this.newPizzaObject !== false) {
          this.newPizzaObject = false;

          new GLTFLoader().load(pizzaGLTF, (gltf) => {
            const pizza = gltf.scene;
            pizza.rotation.z = 0;
            pizza.rotateX(-Math.PI / 2);
            pizza.position.y = 0.05;
            pizza.traverse((o) => {
              if (o.isMesh) {
                const material = new THREE.MeshStandardMaterial({
                  roughness: 0.6,
                  metalness: 0.2,
                });
                material.map = o.material.map;
                o.material = material;

                this.newPizzaObject = () => {
                  const pizza = new THREE.Mesh(o.geometry, o.material);
                  pizza.rotateX(-Math.PI / 2);
                  return pizza;
                };
              }
            });
            this.updatePizza();
          });
        }
      } else {
        if (!this.pizzaGroups) {
          const onePizza = this.newPizzaObject();
          onePizza.rotateZ(Math.random() * Math.PI * 2);
          onePizza.position.set(0, 0.05, 0);
          onePizza.visible = false;
          this.scene.add(onePizza);

          const fourPizzas = new THREE.Group();
          MINI_PIZZAS.forEach(([x, y]) => {
            const subPizza = this.newPizzaObject();
            subPizza.rotateZ(Math.random() * Math.PI * 2);
            subPizza.position.set(
              MINI_PIZZA_DISTANCE * x,
              0.05,
              MINI_PIZZA_DISTANCE * y
            );
            subPizza.scale.set(MINI_PIZZA_SCALE, MINI_PIZZA_SCALE, 1);
            fourPizzas.add(subPizza);
          });
          fourPizzas.visible = false;
          this.scene.add(fourPizzas);

          this.pizzaGroups = [
            {
              pizza: 1,
              group: onePizza,
            },
            {
              pizza: 4,
              group: fourPizzas,
            },
          ];
        }

        this.pizzaGroups.forEach((pg) => {
          pg.group.visible = pg.pizza == this.pizza;
        });

        this.initPizzaCoefficient();
      }
    },
    initOven(three) {
      // Pizza
      this.updatePizza();

      this.heatingCoilWarmMaterial = new THREE.MeshBasicMaterial({
        color: "orange",
      });
      this.heatingCoilColdMaterial = new THREE.MeshBasicMaterial({
        color: "grey",
      });
      const coilGeometry = new CoilBufferGeometry(0.2, 10, 3, 0.05);
      this.heatingCoils = this.heaters.map((h, i) => {
        const mesh = new THREE.Mesh(coilGeometry, this.heatingCoilColdMaterial);
        mesh.rotateY((-i * Math.PI) / 2);
        mesh.rotateX(Math.PI / 2);
        mesh.translateOnAxis(new THREE.Vector3(1, -1, -0.1), 5);
        this.scene.add(mesh);
        return mesh;
      });

      const sideMaterial = new THREE.MeshStandardMaterial({
        color: 0x666666,
        roughness: 0.6,
        metalness: 0.9,
        side: THREE.BackSide,
      });
      const sideGeometry = new THREE.PlaneBufferGeometry(10.6, 4.3);
      //Sides
      [0, 1, 2].forEach((i) => {
        const mesh = new THREE.Mesh(sideGeometry, sideMaterial);
        mesh.rotateY(((1 - i) * Math.PI) / 2);
        //mesh.rotateZ(Math.PI / 2);
        mesh.translateOnAxis(new THREE.Vector3(0, (4 - 2.15) / 5.3, 1), 5.3);
        this.scene.add(mesh);
      });

      RectAreaLightUniformsLib.init();

      const smallLight = new THREE.RectAreaLight(0xf0ee99, 10, 3, 1.5);
      smallLight.position.set(-3, 3, 5);
      this.scene.add(smallLight);

      smallLight.add(
        new THREE.Mesh(
          new THREE.PlaneGeometry(smallLight.width, smallLight.height),
          new MeshBasicMaterial({
            color: smallLight.color,
            side: THREE.BackSide,
          })
        )
      );

      const windowLight = new THREE.RectAreaLight(0xccdbff, 5, 10, 8);
      windowLight.position.set(0, 4, -5);
      windowLight.rotateY(Math.PI);
      this.scene.add(windowLight);

      //windowLight.add(new RectAreaLightHelper(windowLight));
    },
    initScene(three) {
      this.three = three;
      const group = new THREE.Group();
      group.position.set(0, -.5, 0);
      three.scene.add(group);
      this.scene = group;
      window.pizza = this;

      three.camera.position.set(5, 6, -20);
      three.camera.lookAt(0, 0, 0);

      three.renderer.setClearColor(new THREE.Color(0xffffff), 1);

      {
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.1);
        this.scene.add(ambientLight);
      }

      this.initCompute(three);

      this.initGraph(three);

      this.initOven(three);

      const planeMaterial = new THREE.MeshBasicMaterial();
      planeMaterial.side = THREE.DoubleSide;
      planeMaterial.map = this.colorTarget.texture;
      const floor = new THREE.Mesh(
        new THREE.PlaneBufferGeometry(10, 10),
        planeMaterial
      );
      floor.rotateX(-Math.PI / 2);
      this.scene.add(floor);

      this.$emit("init-scene", this, three);
    },
    tick(three, dt) {
      dt *= this.speed;
      if (dt >= 0.9) dt = 0.9;
      this.jacobi(dt);
      this.jacobiCompute.doRenderTarget(this.colorShader, this.colorTarget);

      this.heatingCoils.forEach((c, i) => {
        c.material = this.heaters[i]
          ? this.heatingCoilWarmMaterial
          : this.heatingCoilColdMaterial;
      });
    },
  },
};
</script>
